import type { Role } from '@/lib/api/types';

/** Privilege order: a higher index outranks a lower one. */
const RANK: Record<Role, number> = {
  member: 0,
  editor: 1,
  admin: 2,
};

/** True when `role` meets or exceeds `required`. */
export function hasRole(role: Role | undefined, required: Role): boolean {
  if (!role) return false;
  return RANK[role] >= RANK[required];
}

/**
 * Maps Cognito `cognito:groups` to a single application Role with precedence
 * admin > editor > member. Unknown or empty groups fall back to 'member'.
 */
export function roleFromGroups(groups: string[] | undefined): Role {
  if (!groups || groups.length === 0) return 'member';
  const normalized = new Set(groups.map((g) => g.toLowerCase()));
  if (normalized.has('admin')) return 'admin';
  if (normalized.has('editor')) return 'editor';
  return 'member';
}

export function isAdmin(role: Role | undefined): boolean {
  return hasRole(role, 'admin');
}

/**
 * Can create and edit presentations (editor or admin). Not a knowledge-base
 * gate: the shared acervo is curated by admins alone — see {@link isAdmin} —
 * and everybody manages their own shelf.
 */
export function canEdit(role: Role | undefined): boolean {
  return hasRole(role, 'editor');
}

const ROLE_LABELS: Record<Role, string> = {
  admin: 'Administrador',
  editor: 'Editor',
  member: 'Membro',
};

export function roleLabel(role: Role): string {
  return ROLE_LABELS[role];
}
